<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { bbox } from '@turf/turf'
import { MapSource, MapLayer } from '@/components/map'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'
import { buildLulcTileUrl } from '@/lib/tileUrl'

const store = useStore()
const map = computed(() => props.map)
const layerManager = useLayerManager(map)
const orgId = computed(() => store.getters['org/getSelected'])

const props = defineProps({
  map: { type: Object, required: true },
  beforeId: { type: String, required: true },
  groupId: { type: String, required: true },
  layerId: { type: String, required: true },
  // Parent layers.code — keys the vuex active-month slice.
  parentCode: { type: String, required: true },
  months: { type: Array, default: () => [] },
  isVisible: { type: Boolean, default: true },
  options: { type: Object, default: () => ({}) },
  aoi: { type: Object, default: null },
})

const emit = defineEmits(['isLoaded'])

const doneMonths = computed(() =>
  props.months
    .filter(m => Number(m.status) === 0 && m.code)
    .slice()
    .sort((a, b) => String(a.classification_month).localeCompare(String(b.classification_month)))
)

const storeActiveMonth = computed(() => store.getters['layers/getLulcActiveMonth'](props.parentCode))

const activeMonth = computed(() => {
  const codeFromStore = storeActiveMonth.value
  if (codeFromStore) {
    const match = doneMonths.value.find(m => m.code === codeFromStore)
    if (match) return match
  }
  return doneMonths.value[doneMonths.value.length - 1] || null
})

const tileUrl = computed(() => {
  const code = activeMonth.value?.code
  if (!code || !orgId.value) return null
  return buildLulcTileUrl(orgId.value, code, activeMonth.value)
})

const rasterSourceId = computed(() => createSourceId(props.groupId, `lulc-classified-source-${activeMonth.value?.code || 'none'}`))
const rasterLayerId = computed(() => createLayerId(props.groupId, `lulc-classified-${activeMonth.value?.code || 'none'}`))
const borderSourceId = computed(() => createSourceId(props.groupId, 'lulc-classified-border-source'))
const borderLayerId = computed(() => createLayerId(props.groupId, 'lulc-classified-border'))

const aoiGeoJson = computed(() => {
  if (!props.aoi) return null
  return { type: 'Feature', geometry: props.aoi, properties: {} }
})

// Mapbox skips requests for tiles fully outside this bbox — kills the
// viewport-wide 404 storm for layers covering a small patch.
const layerBounds = computed(() => {
  if (!aoiGeoJson.value) return null
  try {
    return bbox(aoiGeoJson.value)
  } catch (e) {
    return null
  }
})

let registeredRasterId = null

const registerActiveRaster = () => {
  if (registeredRasterId) {
    layerManager.unregisterLayer(props.groupId, registeredRasterId)
    registeredRasterId = null
  }
  if (rasterLayerId.value) {
    layerManager.registerLayer(props.groupId, rasterLayerId.value)
    registeredRasterId = rasterLayerId.value
  }
}

onMounted(() => {
  layerManager.registerLayer(props.groupId, borderLayerId.value)
  registerActiveRaster()
  emit('isLoaded', true)
})

onUnmounted(() => {
  if (registeredRasterId) {
    layerManager.unregisterLayer(props.groupId, registeredRasterId)
    registeredRasterId = null
  }
  layerManager.unregisterLayer(props.groupId, borderLayerId.value)
})

watch(activeMonth, registerActiveRaster)

watch(tileUrl, (newUrl) => {
  if (!newUrl || !map.value) return
  const src = map.value.getSource(rasterSourceId.value)
  if (src && typeof src.setTiles === 'function') {
    src.setTiles([newUrl])
  }
})
</script>

<template>
  <div v-if="map">
    <MapSource
      v-if="tileUrl"
      :key="activeMonth?.code"
      :map="map"
      type="raster"
      :id="rasterSourceId"
      :options="{
        tiles: [tileUrl],
        tileSize: 256,
        ...(layerBounds ? { bounds: layerBounds } : {})
      }"
    >
      <MapLayer
        :map="map"
        :id="rasterLayerId"
        type="raster"
        :source="rasterSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="{
          ...options,
          layout: {
            visibility: isVisible ? 'visible' : 'none',
          },
          paint: {
            'raster-opacity': 0.85,
          },
        }"
      />
    </MapSource>

    <MapSource
      v-if="aoiGeoJson"
      :map="map"
      type="geojson"
      :id="borderSourceId"
      :data="aoiGeoJson"
    >
      <MapLayer
        :map="map"
        :id="borderLayerId"
        type="line"
        :interactive="false"
        :source="borderSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="{
          paint: {
            'line-color': '#10b981',
            'line-width': 2,
            'line-opacity': 0.9,
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none',
          },
        }"
      />
    </MapSource>
  </div>
</template>
